﻿import { useState, useCallback, useRef, useEffect } from 'react';
import { useApp } from '@/store/AppContext';
import type {
  AgentTask,
  AgentFile,
  AgentCapability,
  AgentSourceChip,
  AgentRecurringProposal,
  AgentIntentDecision,
  AgentMemorySummary,
  AgentSelectedCapability,
  AgentStructuredOutput,
  AgentWorkflowSummary,
  AgentSemanticEvent,
  LongTermMemoryReference,
} from '@/types';
import { v4 as uuidv4 } from 'uuid';
import { getSupabase } from '@/hooks/useAuth';
import { getAuthHeaders } from '@/lib/api';
import { initialConversationRunState } from '@/lib/conversationRuntimeReducer';
import {
  fetchAgentRunEvents,
  fetchAgentRunState,
  normalizeRunStateComputerFrames,
  normalizeRunStateEvents,
  normalizeRunStateFiles,
  normalizeRunStatePlanningTasks,
  requestAgentRunResume,
  selectHydratedAgentContent,
  type RawComputerFrame,
} from '@/services/agentRunState';
import type { ConversationRunState, RuntimeArtifact } from '@/types/conversationRuntime';
import { filterReadyConnectorIds } from '@/shared/connectors/availability';

export type AgentMode = 'safe' | 'power';
export type AgentStatus = 'idle' | 'connecting' | 'planning' | 'running' | 'reconnecting' | 'recovering' | 'waiting_approval' | 'needs_takeover' | 'takeover_active' | 'completed' | 'completed_with_warnings' | 'failed' | 'fatal_failed' | 'stopped' | 'interrupted' | 'resume_available' | 'stale';

export interface AgentLogEntry {
  id: string;
  type: 'thinking' | 'tool_call' | 'tool_result' | 'log' | 'error' | 'file_change';
  message: string;
  tool?: string;
  args?: Record<string, unknown>;
  success?: boolean;
  recoverable?: boolean;
  nextAction?: string;
  timestamp: number;
}

export interface ActionChip {
  id: string;
  tool: string;
  label: string;
  success?: boolean;
  timestamp: number;
}

export interface PlanningTask {
  id?: number;
  label: string;
  status: 'pending' | 'active' | 'done' | 'error';
  tool_hint?: string;
  // Action chips shown under this step (Manus-style)
  chips: ActionChip[];
  // Prose text emitted while this step was active
  prose?: string;
  // Short narrative updates shown inside the chat message, Manus-style.
  updates?: string[];
}

export interface AgentBrowserFrame {
  id: string;
  screenshot?: string;
  url?: string;
  title?: string;
  message?: string;
  action?: string;
  step?: number;
  status: 'starting' | 'running' | 'done' | 'error' | 'takeover';
  timestamp: number;
}

export interface AgentBrowserSession {
  status: 'idle' | 'active' | 'needs_takeover' | 'takeover_active' | 'done' | 'error';
  step: number;
  url: string;
  title: string;
  screenshot: string | null;
  lastAction: string;
  lastMessage: string;
  error: string | null;
  updatedAt: number | null;
}

export interface AgentTerminalFrame {
  id: string;
  command: string;
  output?: string;
  error?: string;
  status: 'running' | 'done' | 'error';
  timestamp: number;
}

export interface AgentSubagentRuntime {
  id: string;
  role: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'fallback';
  task: string;
  summary?: string;
  warning?: string;
  duration_ms?: number;
}

export interface AgentState {
  runId: string | null;
  status: AgentStatus;
  logs: AgentLogEntry[];
  files: { path: string; action: 'created' | 'modified' }[];
  currentTask: string;
  pendingApproval: {
    tool: string;
    args: Record<string, unknown>;
    description: string;
    riskLevel?: string;
    reason?: string;
  } | null;
  mode: AgentMode;
  // Live oppgave-kort som vises i chat-meldingen
  liveTasks: AgentTask[];
  liveFiles: AgentFile[];
  agentMessageId: string | null;
  suggestions: string[];
  // High-level planning tasks from LLM breakdown
  planningTasks: PlanningTask[];
  // Current action label (Thinking / Writing / Running etc.)
  currentActionLabel: string;
  // Timestamp when agent started (for elapsed timer)
  startedAt: number | null;
  // Run metrics (#30)
  metrics: { total_tokens?: number; duration_seconds?: number; tool_calls?: Record<string, number> } | null;
  // Current active step index (for chip routing)
  activeStepIndex: number;
  capabilities?: Record<string, AgentCapability>;
  unavailableConnectors?: string[];
  sourcePack?: AgentSourceChip[];
  responseMode?: AgentIntentDecision['response_mode'];
  recurringProposal?: AgentRecurringProposal | null;
  intentDecision?: AgentIntentDecision;
  selectedCapabilities?: AgentSelectedCapability[];
  preferredTools?: string[];
  longTermMemoryReferences?: LongTermMemoryReference[];
  semanticEvents: AgentSemanticEvent[];
  subagentRuntime: AgentSubagentRuntime[];
  browserFrame: AgentBrowserFrame | null;
  browserEvents: AgentBrowserFrame[];
  browserSession: AgentBrowserSession | null;
  pauseReason: string | null;
  terminalFrames: AgentTerminalFrame[];
  runtime: ConversationRunState;
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://sineapi-production-8db6.up.railway.app';
const WS_BASE = API_BASE.replace('https://', 'wss://').replace('http://', 'ws://');

function getTaskLabel(tool: string, args: Record<string, unknown>): string {
  switch (tool) {
    case 'write_file': return `Skriver fil: ${args.path ?? ''}`;
    case 'read_file': return `Leser fil: ${args.path ?? ''}`;
    case 'list_files': return `Lister filer: ${args.path ?? '.'}`;
    case 'terminal': return `Terminal: ${String(args.command ?? '').slice(0, 60)}`;
    case 'web_search': return `Søker: ${args.query ?? ''}`;
    case 'gmail_read': return `Leser Gmail: ${args.query ?? 'is:unread'}`;
    case 'gmail_send': return `Sender e-post: ${args.to ?? ''}`;
    case 'github_list_repos': return 'Lister GitHub-repoer';
    case 'github_read_file': return `Leser GitHub-fil: ${args.repo ?? ''}/${args.path ?? ''}`;
    case 'github_push_file': return `Pusher GitHub-fil: ${args.repo ?? ''}/${args.path ?? ''}`;
    case 'github_create_repo': return `Oppretter GitHub-repo: ${args.name ?? ''}`;
    case 'deploy_preview': return `Publiserer preview: ${args.name ?? ''}`;
    case 'mcp_list_tools': return `Lister MCP-tools: ${args.server_id ?? 'alle servere'}`;
    case 'mcp_call_tool': return `Kjører MCP-tool: ${args.tool_name ?? ''}`;
    case 'run_code': return `Kjører kode`;
    case 'analyze_data': return `Analyserer data`;
    default: return `${tool}: ${JSON.stringify(args).slice(0, 60)}`;
  }
}

function getFileType(filename: string): AgentFile['type'] {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (['md', 'mdx'].includes(ext)) return 'markdown';
  if (ext === 'csv') return 'csv';
  if (ext === 'json') return 'json';
  if (['html', 'htm'].includes(ext)) return 'html';
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'document';
  if (['xls', 'xlsx'].includes(ext)) return 'spreadsheet';
  if (['js', 'ts', 'tsx', 'jsx', 'py', 'rs', 'go', 'java', 'css', 'yaml', 'yml', 'sh'].includes(ext)) return 'code';
  if (['zip', 'tar', 'gz', 'rar', '7z'].includes(ext)) return 'archive';
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
  if (['txt', 'log'].includes(ext)) return 'text';
  return 'other';
}

function getArtifactMimeType(fileType: AgentFile['type'], filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (fileType === 'markdown') return 'text/markdown';
  if (fileType === 'text') return 'text/plain';
  if (fileType === 'csv') return 'text/csv';
  if (fileType === 'json') return 'application/json';
  if (fileType === 'html') return 'text/html';
  if (fileType === 'pdf') return 'application/pdf';
  if (fileType === 'document') return ext === 'doc'
    ? 'application/msword'
    : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (fileType === 'spreadsheet') return ext === 'xls'
    ? 'application/vnd.ms-excel'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (fileType === 'image') return `image/${ext === 'jpg' ? 'jpeg' : ext || '*'}`;
  if (fileType === 'archive') return 'application/zip';
  if (fileType === 'code') return 'text/plain';
  return 'application/octet-stream';
}

function getArtifactPreview(fileType: AgentFile['type'], action: 'created' | 'modified'): string {
  const verb = action === 'created' ? 'klar' : 'oppdatert';
  if (fileType === 'markdown') return `Markdown-dokument er ${verb}.`;
  if (fileType === 'code') return `Kodefil er ${verb}.`;
  if (fileType === 'csv' || fileType === 'spreadsheet') return `Datatabell er ${verb}.`;
  if (fileType === 'json') return `JSON-fil er ${verb}.`;
  if (fileType === 'html') return `HTML-side er ${verb}.`;
  if (fileType === 'pdf' || fileType === 'document') return `Dokument er ${verb}.`;
  if (fileType === 'image') return `Bilde er ${verb}.`;
  if (fileType === 'archive') return `Arkiv er ${verb}.`;
  return `Fil er ${verb}.`;
}

const LIVE_AGENT_STATUSES = new Set<AgentStatus>([
  'connecting',
  'planning',
  'running',
  'reconnecting',
  'recovering',
  'waiting_approval',
  'needs_takeover',
  'takeover_active',
]);

const TERMINAL_AGENT_STATUSES = new Set<AgentStatus>([
  'completed',
  'completed_with_warnings',
  'failed',
  'fatal_failed',
  'stopped',
]);

function isLiveAgentStatus(status: AgentStatus): boolean {
  return LIVE_AGENT_STATUSES.has(status);
}

function normalizeAgentStatus(value: unknown, fallback: AgentStatus = 'running'): AgentStatus {
  const status = String(value ?? '').toLowerCase();
  if (
    status === 'idle'
    || status === 'connecting'
    || status === 'planning'
    || status === 'running'
    || status === 'reconnecting'
    || status === 'recovering'
    || status === 'waiting_approval'
    || status === 'needs_takeover'
    || status === 'takeover_active'
    || status === 'completed'
    || status === 'completed_with_warnings'
    || status === 'failed'
    || status === 'fatal_failed'
    || status === 'stopped'
    || status === 'interrupted'
    || status === 'resume_available'
    || status === 'stale'
  ) {
    return status;
  }
  return fallback;
}

function runtimeStatusFromAgentStatus(status: AgentStatus): ConversationRunState['status'] {
  if (status === 'idle') return 'pending';
  if (status === 'planning') return 'running';
  if (status === 'needs_takeover' || status === 'takeover_active') return status;
  if (status === 'completed_with_warnings') return 'completed';
  if (status === 'fatal_failed') return 'failed';
  return status as ConversationRunState['status'];
}

function browserSessionFromFrame(
  previous: AgentBrowserSession | null,
  frame: AgentBrowserFrame | null,
  statusOverride?: AgentBrowserSession['status'],
): AgentBrowserSession | null {
  if (!frame && !previous && !statusOverride) return null;
  const nextStatus: AgentBrowserSession['status'] = statusOverride
    ?? (frame?.status === 'takeover'
      ? 'takeover_active'
      : frame?.status === 'done'
        ? 'done'
        : frame?.status === 'error'
          ? 'error'
          : previous?.status ?? 'active');
  return {
    status: nextStatus,
    step: frame?.step ?? previous?.step ?? 0,
    url: frame?.url ?? previous?.url ?? '',
    title: frame?.title ?? previous?.title ?? '',
    screenshot: frame?.screenshot ?? previous?.screenshot ?? null,
    lastAction: frame?.action ?? previous?.lastAction ?? '',
    lastMessage: frame?.message ?? previous?.lastMessage ?? '',
    error: frame?.status === 'error' ? frame.message ?? previous?.error ?? null : null,
    updatedAt: frame?.timestamp ?? Date.now(),
  };
}

function buildAgentFileFromPath(filePath: string, runId: string | null, _action: 'created' | 'modified'): AgentFile {
  const name = filePath.split('/').pop() ?? filePath;
  const type = getFileType(filePath);
  return {
    name,
    path: filePath,
    type,
    runId: runId ?? undefined,
    downloadUrl: runId ? `${API_BASE}/api/agent/${runId}/download/${filePath}` : undefined,
  };
}

function buildAgentFileFromPayload(data: Record<string, unknown>, runId: string | null, _action: 'created' | 'modified'): AgentFile | null {
  const path = asText(data.path || data.file_path || data.name);
  if (!path) return null;
  const name = asText(data.name) || path.split('/').pop() || path;
  const rawType = asText(data.type);
  const type = rawType && ['markdown', 'code', 'archive', 'image', 'text', 'csv', 'json', 'html', 'pdf', 'document', 'spreadsheet', 'file', 'other'].includes(rawType)
    ? rawType as AgentFile['type']
    : getFileType(path);
  const resolvedRunId = asText(data.run_id || data.runId) || runId;
  const isReadable = typeof data.isReadable === 'boolean'
    ? data.isReadable
    : typeof data.is_readable === 'boolean'
      ? data.is_readable
      : undefined;
  return {
    name,
    path,
    type,
    size: asText(data.size) || undefined,
    content: asText(data.content) || undefined,
    runId: resolvedRunId ?? undefined,
    mediaType: asText(data.mediaType || data.media_type || data.mime_type) || undefined,
    viewer: asText(data.viewer) || undefined,
    previewKind: asText(data.previewKind || data.preview_kind) || undefined,
    isReadable,
    downloadUrl: asText(data.downloadUrl || data.download_url)
      || (resolvedRunId ? `${API_BASE}/api/agent/${resolvedRunId}/download/${path}` : undefined),
  };
}

function agentFileToRuntimeArtifact(file: AgentFile, runId: string | null, action: 'created' | 'modified'): RuntimeArtifact {
  return {
    id: `${runId ?? 'active'}:${file.path}`,
    runId: runId ?? 'active',
    name: file.name,
    type: file.type,
    path: file.path,
    mimeType: file.mediaType || getArtifactMimeType(file.type, file.name),
    mediaType: file.mediaType || getArtifactMimeType(file.type, file.name),
    viewer: file.viewer,
    previewKind: file.previewKind,
    isReadable: file.isReadable,
    preview: file.content ? file.content.slice(0, 420) : getArtifactPreview(file.type, action),
    downloadUrl: file.downloadUrl,
  };
}

function upsertArtifact(artifacts: RuntimeArtifact[], next: RuntimeArtifact): RuntimeArtifact[] {
  const index = artifacts.findIndex(artifact => artifact.id === next.id || artifact.path === next.path);
  if (index === -1) return [...artifacts, next];
  const copy = artifacts.slice();
  copy[index] = { ...copy[index], ...next };
  return copy;
}

function generateIntroText(_task: string): string {
  // Return empty string â€” the live status line will show real-time updates
  // and the final AI response will populate the content when complete
  return '';
}

function repairMojibake(text: string): string {
  let repaired = text;
  const replacements: Array<[string, string]> = [
    ['ÃƒÂ¥', 'å'], ['ÃƒÂ¸', 'ø'], ['ÃƒÂ¦', 'æ'], ['ÃƒÂ…', 'Å'], ['ÃƒÂ˜', 'Ø'], ['ÃƒÂ†', 'Æ'],
    ['Ã¥', 'å'], ['Ã¸', 'ø'], ['Ã¦', 'æ'], ['Ã…', 'Å'], ['Ã˜', 'Ø'], ['Ã†', 'Æ'],
    ['ÃƒÂ©', 'é'], ['Ã©', 'é'], ['ÃƒÂ¨', 'è'], ['Ã¨', 'è'],
    ['âœ“', '✓'], ['âœ—', 'Feil:'], ['â€¦', '...'], ['â€”', '—'], ['â€“', '–'],
    ['Ã‚Â·', '·'], ['Â·', '·'], ['Ã¢â‚¬Â¦', '...'],
  ];
  for (let i = 0; i < 3; i += 1) {
    const before = repaired;
    for (const [bad, good] of replacements) repaired = repaired.replaceAll(bad, good);
    if (before === repaired) break;
  }
  return repaired
    .replaceAll('Kjører', 'Kjører')
    .replaceAll('Søker', 'Søker')
    .replaceAll('forsøk', 'forsøk')
    .replaceAll('Fullført', 'Fullført')
    .replaceAll('fullført', 'fullført')
    .replace(/\p{Emoji_Presentation}|\uFE0F/gu, '')
    .trim();
}

function asText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function compactActionTarget(value: string): string {
  return repairMojibake(value)
    .replace(/^https?:\/\/(www\.)?/i, '')
    .replace(/[?#].*$/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 96);
}

function cleanNarrativeUpdate(value: string): string {
  const text = repairMojibake(value)
    .replace(/^[-*]\s*/, '')
    .replace(/^(Kjører|Starter|Bruker)\s+(web_search|browser|terminal|run_code|write_file|read_file|list_files)\s*:?\s*/i, '')
    .replace(/^(web_search|browser|terminal|run_code|write_file|read_file|list_files)\s*:?\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';
  if (/^(Step\s+\d+|Success\b|Partial\b|Failed\b|Unknown\b|Error\b|Clicked\b|Ready to\b|Looking at\b|The URL\b|I need to\b|I entered\b|Found the\b|Søkefeil|E2B terminal-feil|Command exited|LLM-feil|Advarsel)/i.test(text)) return '';
  if (/^(<!doctype|<\/?(html|head|body|meta|script|style|link)\b|%?\s*(total|received|xferd|average|speed|time)\b|curl\s+|wget\s+)/i.test(text)) return '';
  if (/https?:\/\/(html|lite)\.duckduckgo\.com/i.test(text)) return '';
  if (/DuckDuckGo.*(RateLimit|202)|context deadline exceeded|_asyncio\.Task|CAPTCHA|Cloudflare|privacy dialog|cookie|search input field|button index|browser-use|about:blank|pydantic|errors\.pydantic\.dev/i.test(text)) return '';
  return text.length > 360 ? `${text.slice(0, 357).trim()}...` : text;
}

function appendNarrativeUpdate(task: PlanningTask, update: string): PlanningTask {
  const text = cleanNarrativeUpdate(update);
  if (!text) return task;
  const updates = task.updates ?? [];
  if (updates.some(existing => existing.toLowerCase() === text.toLowerCase())) {
    return task;
  }
  return {
    ...task,
    prose: task.prose ?? text,
    updates: [...updates, text].slice(-10),
  };
}

function compactObservation(value: string): string {
  return cleanNarrativeUpdate(value)
    .replace(/\*\*/g, '')
    .replace(/^[-*]\s+/, '')
    .replace(/^\d+\.\s+/, '')
    .trim();
}

function extractUsefulObservation(rawValue: unknown): string {
  const raw = repairMojibake(asText(rawValue));
  if (!raw) return '';
  const trimObservationWrappers = (line: string): string => {
    const wrappers = new Set(['"', "'", '`', ',', '{', '}', '[', ']']);
    let start = 0;
    let end = line.length;
    while (start < end && wrappers.has(line[start])) start += 1;
    while (end > start && wrappers.has(line[end - 1])) end -= 1;
    return line.slice(start, end).trim();
  };

  const lines = raw
    .replace(/\\n/g, '\n')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(trimObservationWrappers)
    .filter(line => !/^(web_search|browser|terminal|Søkefeil|E2B|Command exited|Traceback|HTTP Request|Step\s+\d+|Success\b|Partial\b|Failed\b|Unknown\b|Ready to\b|I need to\b|I entered\b|Found the\b)/i.test(line))
    .filter(line => !/^(<!doctype|<\/?(html|head|body|meta|script|style|link)\b|%?\s*(total|received|xferd|average|speed|time)\b|curl\s+|wget\s+)/i.test(line))
    .filter(line => !/https?:\/\/(html|lite)\.duckduckgo\.com/i.test(line))
    .filter(line => !/DuckDuckGo.*(RateLimit|202)|context deadline exceeded|_asyncio\.Task|CAPTCHA|Cloudflare|privacy dialog|cookie|search input field|button index|browser-use|about:blank|pydantic|errors\.pydantic\.dev/i.test(line));

  const signalPatterns = [
    /\b(found|extracted|visible|completed|success|members|companies|bedrifter|ansatte|omsetning|bransje|kontakt|telefon|epost|e-post|kilde|resultat|funn)\b/i,
    /\b\d+\s+(medlemmer|bedrifter|companies|ansatte)\b/i,
    /\b[A-ZÆØÅ0-9][A-ZÆØÅ0-9 .&-]{2,}\s+AS\b/,
  ];

  const signal = lines.find(line => signalPatterns.some(pattern => pattern.test(line)));
  const fallback = lines.find(line => line.length > 24 && !/^(success|true|false|null)$/i.test(line));
  const selected = compactObservation(signal || fallback || '');
  return selected.length > 360 ? `${selected.slice(0, 357).trim()}...` : selected;
}

function getTerminalCommand(tool: string, args: Record<string, unknown>): string {
  if (tool === 'run_python') return asText(args.code || args.script || args.command).slice(0, 500);
  return asText(args.command || args.cmd || args.code || args.path || args.query).slice(0, 500);
}

function semanticToolVerb(tool: string): string {
  const labels: Record<string, string> = {
    web_search: 'søker på nettet',
    browser: 'bruker nettleseren',
    terminal: 'kjører terminalen',
    write_file: 'skriver fil',
    read_file: 'leser fil',
    list_files: 'sjekker filene',
    deploy_preview: 'publiserer forhåndsvisning',
    run_code: 'kjører kode',
    analyze_data: 'analyserer data',
  };
  return labels[tool] ?? `bruker ${tool.replaceAll('_', ' ')}`;
}

function semanticToolText(tool: string, args: Record<string, unknown>, label?: string): string {
  const query = compactActionTarget(asText(args.query || args.url || args.command || args.path || args.name || label));
  if (tool === 'web_search') return query ? `Jeg søker etter kilder om ${query}.` : 'Jeg søker etter oppdatert informasjon.';
  if (tool === 'browser') return query ? `Jeg åpner ${query} og ser etter konkrete funn.` : 'Jeg bruker nettleseren for å hente live informasjon.';
  if (tool === 'terminal') return query ? `Jeg kjører en terminalkommando for å bearbeide resultatet.` : 'Jeg bruker terminalen for å verifisere arbeidet.';
  if (tool === 'write_file') return query ? `Jeg skriver leveransen til ${query}.` : 'Jeg skriver leveransefilen.';
  if (tool === 'read_file') return query ? `Jeg leser ${query} for å hente kontekst.` : 'Jeg leser filgrunnlaget.';
  if (tool === 'list_files') return 'Jeg sjekker hvilke filer som finnes i arbeidsområdet.';
  if (tool === 'deploy_preview') return 'Jeg klargjør en forhåndsvisning av leveransen.';
  return label ? repairMojibake(label) : `Jeg ${semanticToolVerb(tool)}.`;
}

function semanticResultText(tool: string, success: boolean, data: Record<string, unknown>): string {
  const userMessage = repairMojibake(asText(data.user_message));
  const nextAction = repairMojibake(asText(data.next_action));
  const delivery = data.delivery && typeof data.delivery === 'object' ? data.delivery as Record<string, unknown> : undefined;
  const source = data.source && typeof data.source === 'object' ? data.source as Record<string, unknown> : undefined;
  const summary = extractUsefulObservation(
    data.semantic_output
    || data.summary
    || data.user_message
    || delivery?.summary
    || source?.title
    || data.message
    || data.output
    || data.result
  );
  if (success) {
    if (summary) return summary;
    if (tool === 'web_search') return 'Jeg fant relevante kilder og snevrer inn grunnlaget.';
    if (tool === 'browser') return 'Jeg leste siden og bruker de konkrete funnene i neste steg.';
    if (tool === 'terminal') return 'Terminalsteget er ferdig, og jeg sjekker resultatet.';
    if (tool === 'write_file') return 'Filen er skrevet og klar til leveransen.';
    if (tool === 'read_file') return 'Jeg har lest filen og bruker innholdet videre.';
    return `${semanticToolVerb(tool).replace(/^./, c => c.toUpperCase())} er ferdig.`;
  }
  if (userMessage) return userMessage;
  if (nextAction) return `Denne kilden ga ikke nok, så jeg bytter strategi: ${nextAction}`;
  return `Denne kilden ga ikke nok, så jeg bytter strategi og fortsetter.`;
}

function extractSectionBullets(message: string, headings: string[]): string[] {
  const lines = message.split('\n');
  const normalizedHeadings = headings.map(h => h.toLowerCase());
  const collected: string[] = [];
  let inSection = false;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      if (inSection && collected.length > 0) break;
      continue;
    }

    const normalized = line.toLowerCase().replace(/^#+\s*/, '').trim();
    if (normalizedHeadings.some(h => normalized === h || normalized.startsWith(`${h}:`))) {
      inSection = true;
      continue;
    }

    if (inSection && (/^#+\s+/.test(line) || (/^[A-ZÆØÅ].+/.test(line) && !line.startsWith('- ') && !line.startsWith('* ') && !/^\d+\./.test(line)))) {
      break;
    }

    if (inSection) {
      const bullet = line.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '').trim();
      if (bullet) collected.push(bullet);
    }
  }

  return collected;
}

function deriveAgentRecurringProposal(
  task: string,
  message: string,
  responseMode: AgentIntentDecision['response_mode'],
  sourcePack: AgentSourceChip[],
  structuredOutput?: AgentStructuredOutput,
): AgentRecurringProposal | null {
  const combined = `${task}\n${message}`.toLowerCase();
  const looksRecurring = [
    'hver', 'ukentlig', 'weekly', 'monthly', 'månedlig', 'daglig', 'daily',
    'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'søndag',
  ].some(token => combined.includes(token));

  if (responseMode !== 'monitoring' && !(responseMode === 'report' && looksRecurring) && !(responseMode === 'briefing' && looksRecurring)) {
    return null;
  }

  let frequency: AgentRecurringProposal['frequency'] = 'weekly';
  let dayOfWeek: number | undefined;
  let dayOfMonth: number | undefined;
  let timeOfDay = '09:00';

  const timeMatch = `${task}\n${message}`.match(/(\d{1,2})[:.](\d{2})/);
  if (timeMatch) {
    timeOfDay = `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
  }

  const weekdayMap: Array<[RegExp, number]> = [
    [/\bmandag\b|\bmonday\b/i, 0],
    [/\btirsdag\b|\btuesday\b/i, 1],
    [/\bonsdag\b|\bwednesday\b/i, 2],
    [/\btorsdag\b|\bthursday\b/i, 3],
    [/\bfredag\b|\bfriday\b/i, 4],
    [/\blørdag\b|\blordag\b|\bsaturday\b/i, 5],
    [/\bsøndag\b|\bsondag\b|\bsunday\b/i, 6],
  ];

  if (combined.includes('hver dag') || combined.includes('daily') || combined.includes('daglig')) {
    frequency = 'daily';
  } else if (combined.includes('hver måned') || combined.includes('monthly') || combined.includes('månedlig')) {
    frequency = 'monthly';
    const monthMatch = combined.match(/\bden\s+(\d{1,2})\.|\bon\s+the\s+(\d{1,2})(st|nd|rd|th)?\b/i);
    const dayValue = monthMatch?.[1] ?? monthMatch?.[2];
    if (dayValue) dayOfMonth = Number(dayValue);
  } else {
    frequency = 'weekly';
    for (const [pattern, dow] of weekdayMap) {
      if (pattern.test(combined)) {
        dayOfWeek = dow;
        break;
      }
    }
  }

  const checks = extractSectionBullets(message, ['Hva jeg sjekker', 'What I’ll check every day at 8 AM', 'What I will check every day at 8 AM', 'What I check']);
  const alertConditions = extractSectionBullets(message, ['Når jeg varsler', 'When I’ll alert you in Slack', 'When I will alert you', 'When I alert']);
  const destinations = extractSectionBullets(message, ['Hvor jeg leverer', 'Weekly rollup', 'Fast oppsummering'])
    .filter(item => item.length < 180);
  const rollup = (() => {
    const rollupBullets = extractSectionBullets(message, ['Fast oppsummering', 'Weekly rollup']);
    return rollupBullets.length > 0 ? rollupBullets.join(' ') : undefined;
  })();

  const kind: NonNullable<AgentRecurringProposal['workflowSpec']>['kind'] =
    responseMode === 'briefing'
      ? 'reminder'
      : (responseMode === 'report' || combined.includes('rapport') || combined.includes('report') ? 'report' : 'monitor');

  const connectorName = sourcePack.find(source =>
    ['gmail', 'slack', 'github', 'calendar', 'notion', 'google ads'].includes(source.app.toLowerCase())
  )?.app;

  const derivedChecks = checks.length > 0
    ? checks
    : (structuredOutput?.sections
      ?.filter(section => /hovedtall|observasjoner|top line|problemer|anbefaling/i.test(section.title))
      .flatMap(section => section.items ?? [])
      .slice(0, 4) ?? []);

  const derivedDestinations = destinations.length > 0
    ? destinations
    : (structuredOutput?.delivery?.channels?.map(channel => `Lever via ${channel}`) ?? []);

  const reportTitle = structuredOutput?.headline?.trim();
  const description = structuredOutput?.summary?.trim()
    || message.split('\n').find(line => line.trim())?.trim()
    || task;

  return {
    title: reportTitle && reportTitle.length <= 80
      ? reportTitle
      : (task.length > 80 ? `${task.slice(0, 77).trim()}…` : task),
    description,
    category: responseMode === 'briefing' ? 'Briefing' : (kind === 'report' ? 'Rapportering' : 'Monitorering'),
    frequency,
    dayOfWeek,
    dayOfMonth,
    timeOfDay,
    connectorName,
    agentPrompt: message.trim() || task,
    workflowSpec: {
      kind,
      objective: description,
      triggerLabel: `${frequency === 'daily' ? 'Daglig' : frequency === 'monthly' ? 'Månedlig' : 'Ukentlig'} kl. ${timeOfDay} (norsk tid)`,
      sourceSummary: sourcePack.length > 0
        ? `Bygger på ${sourcePack.slice(0, 3).map(source => source.app).join(', ')}`
        : undefined,
      cadenceSummary: `${frequency === 'daily' ? 'Daglig' : frequency === 'monthly' ? 'Månedlig' : 'Ukentlig'} kl. ${timeOfDay} (norsk tid)`,
      checks: derivedChecks.length > 0 ? derivedChecks : sourcePack.map(source => source.summary).slice(0, 4),
      alertConditions: alertConditions.length > 0 ? alertConditions : undefined,
      destinations: derivedDestinations.length > 0 ? derivedDestinations : undefined,
      rollup: rollup || structuredOutput?.nextStep,
      activationMode: 'auto_run',
    },
  };
}

export function useAgent() {
  const [state, setState] = useState<AgentState>({
    runId: null,
    status: 'idle',
    logs: [],
    files: [],
    currentTask: '',
    pendingApproval: null,
    mode: 'safe',
    liveTasks: [],
    liveFiles: [],
    agentMessageId: null,
    suggestions: [],
    planningTasks: [],
    currentActionLabel: '',
    startedAt: null,
    metrics: null,
    activeStepIndex: 0,
    recurringProposal: null,
    semanticEvents: [],
    subagentRuntime: [],
    browserFrame: null,
    browserEvents: [],
    browserSession: null,
    pauseReason: null,
    terminalFrames: [],
    runtime: initialConversationRunState,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const runIdRef = useRef<string | null>(null);
  const agentMsgIdRef = useRef<string | null>(null);
  const activeTasksRef = useRef<Map<string, AgentTask>>(new Map());
  // #27: Reconnect state
  const reconnectAttemptsRef = useRef<number>(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_RECONNECT_ATTEMPTS = 3;
  const convIdRef = useRef<string | null>(null);
  const taskRef = useRef<string>('');
  const accessTokenRef = useRef<string>('');
  const intentDecisionRef = useRef<AgentIntentDecision | undefined>(undefined);
  const selectedCapabilitiesRef = useRef<AgentSelectedCapability[]>([]);
  const semanticEventsRef = useRef<AgentSemanticEvent[]>([]);
  const receivedCompleteRef = useRef<boolean>(false);
  const lastSequenceByRunRef = useRef<Record<string, number>>({});

  const { addMessage, updateAgentMessage, updateConversationMeta, activeConversationId, activeConversation, createConversation, settings } = useApp();

  useEffect(() => {
    const convId = convIdRef.current;
    const agentMsgId = agentMsgIdRef.current;
    if (!convId || !agentMsgId || state.planningTasks.length === 0) return;
    updateAgentMessage(convId, agentMsgId, { agentPlanningTasks: state.planningTasks });
  }, [state.planningTasks, updateAgentMessage]);

  const addLog = useCallback((entry: Omit<AgentLogEntry, 'id' | 'timestamp'>) => {
    setState(prev => ({
      ...prev,
      logs: [...prev.logs, {
        ...entry,
        message: repairMojibake(entry.message),
        id: uuidv4(),
        timestamp: Date.now(),
      }]
    }));
  }, []);

  const addSemanticEvent = useCallback((
    event: Omit<AgentSemanticEvent, 'id' | 'timestamp'>,
    convId?: string,
    agentMsgId?: string,
  ) => {
    setState(prev => {
      const normalized: AgentSemanticEvent = {
        ...event,
        id: uuidv4(),
        text: repairMojibake(event.text),
        title: event.title ? repairMojibake(event.title) : undefined,
        timestamp: Date.now(),
      };
      const semanticEvents = [...prev.semanticEvents, normalized]
        .filter(item => item.text.trim().length > 0)
        .slice(-80);
      semanticEventsRef.current = semanticEvents;
      if (convId && agentMsgId) {
        updateAgentMessage(convId, agentMsgId, { agentSemanticEvents: semanticEvents });
      }
      return { ...prev, semanticEvents };
    });
  }, [updateAgentMessage]);

  const finalizeAgentMessage = useCallback((
    convId: string,
    agentMsgId: string,
    message: string,
    status: Extract<AgentStatus, 'completed_with_warnings' | 'failed' | 'fatal_failed'> = 'failed',
  ) => {
    const cleanMessage = repairMojibake(message);
    receivedCompleteRef.current = true;
    setState(prev => ({
      ...prev,
      status,
      pendingApproval: null,
      currentActionLabel: status === 'completed_with_warnings' ? 'Fullført med advarsler' : 'Stoppet',
      liveTasks: prev.liveTasks.map(task => task.status === 'running' ? { ...task, status: 'error' as const } : task),
      planningTasks: prev.planningTasks.map(task => task.status === 'active' ? { ...task, status: 'error' as const } : task),
    }));
    addLog({ type: 'error', message: cleanMessage });
    const event = new CustomEvent('agent-complete', {
      detail: {
        convId,
        msgId: agentMsgId,
        files: [] as AgentFile[],
        suggestions: [] as string[],
        intentDecision: intentDecisionRef.current,
        selectedCapabilities: selectedCapabilitiesRef.current,
        memorySummary: undefined as AgentMemorySummary | undefined,
        workflowSummary: undefined as AgentWorkflowSummary | undefined,
        message: cleanMessage,
        status,
      }
    });
    window.dispatchEvent(event);
  }, [addLog]);

  const startAgent = useCallback(async (task: string, mode: AgentMode = 'safe', studioMode: boolean = false, runtimeTask?: string) => {
    if (wsRef.current) wsRef.current.close();
    activeTasksRef.current.clear();
    const effectiveTask = runtimeTask?.trim() || task;

    // SÃ¸rg for aktiv samtale
    let convId = activeConversationId;
    if (!convId) convId = createConversation(settings.model, studioMode ? 'studio' : 'chat');
    convIdRef.current = convId;
    taskRef.current = task;
    reconnectAttemptsRef.current = 0;
    receivedCompleteRef.current = false;
    intentDecisionRef.current = undefined;
    selectedCapabilitiesRef.current = [];
    semanticEventsRef.current = [];
    if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }

    updateConversationMeta(convId, {
      useAgentMode: true,
      ...(studioMode ? { appMode: 'studio' as const } : {}),
    });

    // Legg til bruker-melding
    addMessage(convId, { role: 'user', content: task });

    // Legg til agent-melding med umiddelbar intro-tekst
    const introText = generateIntroText(task);
    const initialSemanticEvent: AgentSemanticEvent = {
      id: uuidv4(),
      kind: 'thought',
      title: 'Tenker',
      text: 'Jeg leser oppgaven og velger beste arbeidsløype.',
      status: 'running',
      timestamp: Date.now(),
    };
    const agentMsgId = addMessage(convId, {
      role: 'agent',
      content: introText,
      isAgentMessage: true,
      agentStatus: 'running',
      agentRunId: undefined,
      agentTasks: [],
      agentFiles: [],
      agentSuggestions: [],
      agentSemanticEvents: [initialSemanticEvent],
      agentSubagentRuntime: [],
    });
    agentMsgIdRef.current = agentMsgId;
    semanticEventsRef.current = [initialSemanticEvent];

    setState({
      runId: null,
      status: 'planning',
      logs: [],
      files: [],
      currentTask: task,
      pendingApproval: null,
      mode,
      liveTasks: [],
      liveFiles: [],
      agentMessageId: agentMsgId,
      suggestions: [],
      planningTasks: [],
      currentActionLabel: 'Tenker',
      startedAt: Date.now(),
      metrics: null,
      activeStepIndex: 0,
      capabilities: undefined,
      unavailableConnectors: [],
      sourcePack: [],
      responseMode: 'default',
      recurringProposal: null,
      intentDecision: undefined,
      selectedCapabilities: [],
      preferredTools: [],
      longTermMemoryReferences: [],
      semanticEvents: [initialSemanticEvent],
      subagentRuntime: [],
      browserFrame: null,
      browserEvents: [],
      browserSession: null,
      pauseReason: null,
      terminalFrames: [],
      runtime: initialConversationRunState,
    });

    try {
      // Hent JWT-token for autentisering
      let accessToken = ''
      try {
        const { data: { session } } = await getSupabase().auth.getSession()
        if (session?.access_token) {
          accessToken = session.access_token
          accessTokenRef.current = accessToken
        }
      } catch { /* no-op */ }

      const res = await fetch(`${API_BASE}/api/agent/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          task: effectiveTask,
          mode,
          enabled_connectors: filterReadyConnectorIds(
            activeConversation?.enabledConnectors ?? [],
            settings.connectorCapabilities ?? {},
            settings.connectorStatuses ?? {},
            settings.connectorDisabled ?? {},
          ),
          conversation_id: convId,
          message_id: agentMsgId,
        }),
      });
      if (!res.ok) throw new Error(`API-feil: ${res.status}`);
      const startData = await res.json();
      const runId = startData.run_id as string;
      const unavailableConnectors = (startData.unavailable_connectors as string[] | undefined) ?? [];
      runIdRef.current = runId;
      updateAgentMessage(convId!, agentMsgId, { agentRunId: runId });
      setState(prev => ({ ...prev, runId, unavailableConnectors }));

      // Inkluder token i WS-URL for autentisering
      const wsUrl = accessToken
        ? `${WS_BASE}/api/agent/ws/${runId}?token=${encodeURIComponent(accessToken)}`
        : `${WS_BASE}/api/agent/ws/${runId}`
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        const userMemory = (settings.userMemory ?? []).map(m => ({ key: m.key, value: m.value }))
        // Build conversation history from the active conversation so the agent
        // has full context when switching from chat mode or continuing a thread.
        // We include user, assistant and agent messages, mapping 'agent' -> 'assistant'
        // since Claude only understands user/assistant roles.
        // We exclude the last message (user task) since the agent receives it separately.
        // The agent intro placeholder has empty content so it's already filtered out.
        const conversationHistory = (activeConversation?.messages ?? [])
          .filter(m => (m.role === 'user' || m.role === 'assistant' || m.role === 'agent'))
          .filter(m => m.content && m.content.trim() !== '' && !m.isStreaming)
          // Exclude the last message (user task message just added above).
          // The agent intro placeholder has empty content so it's already filtered out.
          .slice(0, -1)
          .map(m => ({
            role: (m.role === 'agent' ? 'assistant' : m.role) as 'user' | 'assistant',
            content: m.content,
          }))
        ws.send(JSON.stringify({
          action: 'start',
          task: effectiveTask,
          user_memory: userMemory,
          conversation_history: conversationHistory,
          studio_mode: studioMode,
          conversation_id: convId,
          message_id: agentMsgId,
        }))
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          handleAgentEvent(msg, convId!, agentMsgId);
        } catch (e) {
          console.error('WS parse error:', e);
        }
      };

      ws.onerror = () => {
        addLog({ type: 'log', message: 'Forbindelsen til agent-backend fikk en feil. Jeg prÃƒÂ¸ver ÃƒÂ¥ holde samtalen i live.' });
      };

      ws.onclose = (event) => {
        let shouldFinalizeAfterClose = false;
        const canReconnect = !receivedCompleteRef.current && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS;
        setState(prev => {
          // Only update status if the agent hasn't already reached a terminal state
          // via a proper 'complete' or 'error' event from the backend.
          if (isLiveAgentStatus(prev.status)) {
            // Close code 1000 = normal closure (server finished cleanly)
            if ((event.code === 1000 || event.code === 1001) && !canReconnect) {
              shouldFinalizeAfterClose = !receivedCompleteRef.current;
              return receivedCompleteRef.current ? prev : { ...prev, status: 'recovering', currentActionLabel: 'Kobler til kjøringen igjen' };
            }
            // #27: Unexpected disconnect â€” attempt reconnect
            if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
              const attempt = reconnectAttemptsRef.current + 1;
              reconnectAttemptsRef.current = attempt;
              const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000); // 1s, 2s, 4s
              addLog({ type: 'log', message: `Tilkobling mistet. Prøver igjen om ${delay / 1000}s... (${attempt}/${MAX_RECONNECT_ATTEMPTS})` });
              reconnectTimerRef.current = setTimeout(() => {
                const rid = runIdRef.current;
                if (!rid) return;
                const tok = accessTokenRef.current
                const reconnectWsUrl = tok
                  ? `${WS_BASE}/api/agent/ws/${rid}?token=${encodeURIComponent(tok)}`
                  : `${WS_BASE}/api/agent/ws/${rid}`
                const newWs = new WebSocket(reconnectWsUrl);
                wsRef.current = newWs;
                newWs.onopen = () => {
                  addLog({ type: 'log', message: 'Tilkoblet igjen - fortsetter' });
                  reconnectAttemptsRef.current = 0;
                  setState(prev => ({
                    ...prev,
                    status: 'running',
                    currentActionLabel: prev.currentActionLabel === 'Kobler til kjøringen igjen' ? 'Tenker' : prev.currentActionLabel,
                    runtime: { ...prev.runtime, status: 'running' },
                  }));
                  newWs.send(JSON.stringify({
                    action: 'reconnect',
                    task: taskRef.current,
                    resume: true,
                    user_memory: [],
                    conversation_history: [],
                    last_sequence_number: runIdRef.current ? (lastSequenceByRunRef.current[runIdRef.current] ?? 0) : 0,
                  }));
                };
                newWs.onmessage = (e) => {
                  try {
                    const msg = JSON.parse(e.data);
                    handleAgentEvent(msg, convIdRef.current!, agentMsgIdRef.current!);
                  } catch { /* ignore */ }
                };
                newWs.onerror = () => {
                  addLog({ type: 'log', message: 'Ny tilkobling feilet. Prøver igjen hvis mulig.' });
                };
                newWs.onclose = ws.onclose; // re-use same handler for further reconnects
              }, delay);
              return { ...prev, status: 'reconnecting', currentActionLabel: 'Kobler til kjøringen igjen', runtime: { ...prev.runtime, status: 'reconnecting' } };
            }
            // Max reconnects reached
            return { ...prev, status: 'recovering', currentActionLabel: 'Gjenoppretter fra lagret kjøring', runtime: { ...prev.runtime, status: 'recovering' } };
          }
          return prev;
        });
        if (shouldFinalizeAfterClose && convIdRef.current && agentMsgIdRef.current) {
          finalizeAgentMessage(
            convIdRef.current,
            agentMsgIdRef.current,
            'Forbindelsen til agenten ble brutt før kjøringen ble fullført. Jeg har beholdt samtalen, og vi kan prøve igjen fra her.',
            'failed',
          );
        }
      };
    } catch (err) {
      finalizeAgentMessage(
        convId!,
        agentMsgId,
        `Jeg klarte ikke ÃƒÂ¥ starte agentkjÃƒÂ¸ringen riktig. Feil: ${err instanceof Error ? err.message : String(err)}`,
        'fatal_failed',
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId, createConversation, addMessage, addLog, finalizeAgentMessage, settings.model, updateConversationMeta]);

  const handleAgentEvent = useCallback((
    msg: { type: string; data: Record<string, unknown>; sequence_number?: number },
    _convId: string,
    _agentMsgId: string
  ) => {
    const { type, data } = msg;
    const runId = runIdRef.current;
    const sequenceNumber = typeof msg.sequence_number === 'number' ? msg.sequence_number : undefined;
    if (runId && sequenceNumber !== undefined && sequenceNumber > 0) {
      const lastSequenceNumber = lastSequenceByRunRef.current[runId] ?? 0;
      if (sequenceNumber <= lastSequenceNumber) return;
      lastSequenceByRunRef.current[runId] = sequenceNumber;
      setState(prev => ({
        ...prev,
        runtime: {
          ...prev.runtime,
          runId: prev.runtime.runId ?? runId,
          lastSeqByRun: {
            ...prev.runtime.lastSeqByRun,
            [runId]: sequenceNumber,
          },
        },
      }));
    }

    switch (type) {
      case 'run_state_updated': {
        const restoredFiles = (data.files as AgentFile[] | string[] | undefined) ?? [];
        const agentFiles = restoredFiles
          .map(file => typeof file === 'string' ? buildAgentFileFromPath(file, runIdRef.current, 'created') : file)
          .filter(Boolean) as AgentFile[];
        const restoredEvents = Array.isArray(data.events)
          ? data.events.filter(event => event && typeof event === 'object') as Array<Record<string, unknown>>
          : [];
        const restoredSteps = Array.isArray(data.steps)
          ? data.steps.filter(step => step && typeof step === 'object') as Array<Record<string, unknown>>
          : [];
        const planningTasks = restoredSteps
          .map((step, index): PlanningTask | null => {
            const label = asText(step.label ?? step.text ?? step.title);
            if (!label) return null;
            const rawStatus = asText(step.status).toLowerCase();
            return {
              id: typeof step.id === 'number' ? step.id : index + 1,
              label,
              status: rawStatus === 'done' || rawStatus === 'completed'
                ? 'done'
                : rawStatus === 'error' || rawStatus === 'failed'
                  ? 'error'
                  : rawStatus === 'running' || rawStatus === 'active'
                    ? 'active'
                    : 'pending',
              tool_hint: asText(step.tool_hint ?? step.tool) || undefined,
              chips: [],
              updates: [],
            };
          })
          .filter(Boolean) as PlanningTask[];
        const replayLogs: AgentLogEntry[] = restoredEvents.slice(-60).map((event, index) => {
          const eventData = event.data && typeof event.data === 'object' && !Array.isArray(event.data)
            ? event.data as Record<string, unknown>
            : {};
          const type = asText(event.type || eventData.type) || 'log';
          const message = asText(eventData.message ?? eventData.label ?? eventData.summary ?? eventData.path ?? event.label) || type;
          const logType: AgentLogEntry['type'] =
            type === 'thinking' ? 'thinking'
              : type === 'tool_call' ? 'tool_call'
                : type === 'tool_result' ? 'tool_result'
                  : type === 'file_change' ? 'file_change'
                    : type === 'error' ? 'error'
                      : 'log';
          return {
            id: asText(event.id) || `${runIdRef.current ?? 'run'}:${event.sequence_number ?? index + 1}`,
            type: logType,
            message,
            tool: asText(eventData.tool ?? event.tool) || undefined,
            success: typeof eventData.success === 'boolean' ? eventData.success : undefined,
            timestamp: typeof event.timestamp === 'number'
              ? event.timestamp
              : Date.parse(asText(event.timestamp) || '') || Date.now(),
          };
        });
        const computerFrames = Array.isArray(data.computer_frames)
          ? data.computer_frames.filter((frame): frame is RawComputerFrame => Boolean(frame) && typeof frame === 'object' && !Array.isArray(frame))
          : [];
        const restoredComputer = normalizeRunStateComputerFrames({
          run_id: runIdRef.current ?? 'run',
          computer_frames: computerFrames,
        });
        const pauseReason = asText(data.pause_reason) || null;
        const rawStatus = (data.status as AgentStatus | undefined) ?? (data.new_state as AgentStatus | undefined);
        setState(prev => {
          const restoredStatus = normalizeAgentStatus(
            pauseReason === 'takeover_active' ? 'takeover_active' : pauseReason === 'takeover_required' ? 'needs_takeover' : rawStatus,
            prev.status,
          );
          const nextBrowserFrame = restoredComputer.browserFrame ?? prev.browserFrame;
          return {
            ...prev,
            status: restoredStatus,
            pauseReason,
            liveFiles: agentFiles.length > 0 ? agentFiles : prev.liveFiles,
            planningTasks: planningTasks.length > 0 ? planningTasks : prev.planningTasks,
            logs: replayLogs.length > 0 ? replayLogs : prev.logs,
            browserFrame: nextBrowserFrame,
            browserEvents: restoredComputer.browserEvents.length > 0 ? restoredComputer.browserEvents : prev.browserEvents,
            browserSession: browserSessionFromFrame(
              prev.browserSession,
              nextBrowserFrame,
              restoredStatus === 'takeover_active'
                ? 'takeover_active'
                : restoredStatus === 'needs_takeover'
                  ? 'needs_takeover'
                  : undefined,
            ),
            terminalFrames: restoredComputer.terminalFrames.length > 0 ? restoredComputer.terminalFrames : prev.terminalFrames,
            suggestions: (data.suggestions as string[] | undefined) ?? prev.suggestions,
            metrics: (data.metrics as AgentState['metrics'] | undefined) ?? prev.metrics,
            runtime: {
              ...prev.runtime,
              runId: runIdRef.current ?? prev.runtime.runId,
              status: runtimeStatusFromAgentStatus(restoredStatus),
              persistentText: (data.latest_text as string | undefined) ?? prev.runtime.persistentText,
            },
          };
        });
        break;
      }
      case 'status':
        setState(prev => ({ ...prev, status: (data.status as AgentStatus) || prev.status }));
        if (data.message) {
          const message = data.message as string;
          if (!message.toLowerCase().startsWith('planlegger:')) {
            addLog({ type: 'log', message });
          }
        }
        break;

      case 'task_plan': {
        const steps = data.steps as Array<{ id?: number; label: string; status: string; tool_hint?: string }>;
        if (Array.isArray(steps) && steps.length > 0) {
          const tasks: PlanningTask[] = steps.map((s, i) => ({
            id: s.id ?? i + 1,
            label: s.label,
            status: i === 0 ? 'active' : 'pending',
            tool_hint: s.tool_hint,
            chips: [],
            prose: i === 0 ? `Jeg forstår oppgaven og starter med ${s.label.toLowerCase()}.` : undefined,
            updates: i === 0 ? [`Jeg forstår oppgaven og starter med ${s.label.toLowerCase()}.`] : [],
          }));
          updateAgentMessage(_convId, _agentMsgId, { agentStatus: 'running' });
          setState(prev => ({ ...prev, planningTasks: tasks, currentActionLabel: 'Tenker', activeStepIndex: 0 }));
          addSemanticEvent({
            kind: 'step',
            title: 'Planen er klar',
            text: `Jeg deler dette opp i ${tasks.length} steg og starter med: ${tasks[0]?.label ?? 'første steg'}.`,
            status: 'running',
          }, _convId, _agentMsgId);
        }
        break;
      }

      case 'task_plan_complete': {
        const success = data.success !== false && !['failed', 'fatal_failed'].includes(asText(data.status));
        setState(prev => ({
          ...prev,
          planningTasks: prev.planningTasks.map(t => {
            if (success) return { ...t, status: 'done' as const };
            return t.status === 'done' ? t : { ...t, status: 'error' as const };
          }),
        }));
        break;
      }

      case 'thinking': {
        // #13: Prose between steps â€” attach to active planning task
        const prose = data.message as string;
        addLog({ type: 'thinking', message: prose });
        addSemanticEvent({
          kind: 'thought',
          title: 'Tenker',
          text: prose,
          status: 'running',
        }, _convId, _agentMsgId);
        setState(prev => ({
          ...prev,
          planningTasks: prev.planningTasks.map((t, i) =>
            i === prev.activeStepIndex ? appendNarrativeUpdate(t, prose) : t
          ),
        }));
        break;
      }

      case 'step_advance': {
        const newIdx = data.step_index as number;
        const completedIdx = data.completed_index as number;
        const nextLabel = asText(data.label || data.step || '');
        setState(prev => ({
          ...prev,
          activeStepIndex: newIdx,
          planningTasks: prev.planningTasks.map((t, i) => {
            if (i === completedIdx) return { ...t, status: 'done' as const };
            if (i === newIdx) return {
              ...appendNarrativeUpdate(t, `Nå går jeg videre til ${nextLabel || t.label.toLowerCase()}.`),
              status: 'active' as const,
            };
            return t;
          }),
        }));
        addSemanticEvent({
          kind: 'step',
          title: 'Neste steg',
          text: `Nå går jeg videre til ${nextLabel || 'neste del av oppgaven'}.`,
          status: 'running',
        }, _convId, _agentMsgId);
        break;
      }

      case 'metrics': {
        setState(prev => ({ ...prev, metrics: data.metrics as AgentState['metrics'] }));
        break;
      }

      case 'capabilities': {
        intentDecisionRef.current = data.intent as AgentIntentDecision | undefined;
        selectedCapabilitiesRef.current = (data.selected_capabilities as AgentSelectedCapability[] | undefined) ?? [];
        setState(prev => ({
          ...prev,
          capabilities: data.capabilities as AgentState['capabilities'],
          unavailableConnectors: (data.unavailable_connectors as string[] | undefined) ?? prev.unavailableConnectors,
          intentDecision: intentDecisionRef.current,
          selectedCapabilities: selectedCapabilitiesRef.current,
          preferredTools: (data.preferred_tools as string[] | undefined) ?? prev.preferredTools,
          longTermMemoryReferences: (data.long_term_memory_references as LongTermMemoryReference[] | undefined) ?? prev.longTermMemoryReferences,
        }));
        break;
      }

      case 'multi_agent_schedule': {
        const workers = (data.workers as Array<{ role?: string; task?: string }> | undefined) ?? [];
        const shouldParallelize = Boolean(data.should_parallelize);
        const reason = repairMojibake(asText(data.reason));
        const message = repairMojibake(asText(data.message)) || (
          shouldParallelize
            ? `Jeg deler oppgaven i ${workers.length} parallelle arbeidsstrømmer.`
            : 'Jeg bruker en enkel arbeidsflyt for denne oppgaven.'
        );
        addLog({ type: 'thinking', message });
        addSemanticEvent({
          kind: 'thought',
          title: shouldParallelize ? 'Parallell arbeidsflyt' : 'Arbeidsflyt valgt',
          text: reason ? `${message} ${reason}` : message,
          status: 'running',
        }, _convId, _agentMsgId);
        setState(prev => ({
          ...prev,
          currentActionLabel: shouldParallelize ? 'Parallelle arbeidsstrømmer' : prev.currentActionLabel,
          planningTasks: prev.planningTasks.map((task, index) => index === prev.activeStepIndex
            ? appendNarrativeUpdate(task, message)
            : task),
        }));
        break;
      }

      case 'supervisor_planning': {
        const message = repairMojibake(asText(data.message || 'Jeg setter flere arbeidsstrømmer i gang parallelt.'));
        addLog({ type: 'thinking', message });
        addSemanticEvent({
          kind: 'thought',
          title: 'Parallell arbeidsflyt',
          text: message,
          status: 'running',
        }, _convId, _agentMsgId);
        setState(prev => ({ ...prev, currentActionLabel: 'Parallelle arbeidsstrømmer' }));
        break;
      }

      case 'subtasks_created': {
        const subtasks = (data.subtasks as Array<{ id?: string; role?: string; task?: string }> | undefined) ?? [];
        const count = Number(data.count ?? subtasks.length);
        const message = `Jeg fordeler arbeidet på ${count} parallelle arbeidsstrøm${count === 1 ? '' : 'mer'}.`;
        const runtimes: AgentSubagentRuntime[] = subtasks.map((subtask, subIndex) => ({
          id: String(subtask.id ?? `${Date.now()}-${subIndex}`),
          role: String(subtask.role ?? 'agent'),
          status: 'queued',
          task: repairMojibake(asText(subtask.task || subtask.role || 'Subagent')),
        }));
        addLog({ type: 'log', message });
        addSemanticEvent({
          kind: 'step',
          title: 'Arbeidet deles opp',
          text: message,
          status: 'running',
        }, _convId, _agentMsgId);
        updateAgentMessage(_convId, _agentMsgId, { agentSubagentRuntime: runtimes });
        setState(prev => ({
          ...prev,
          currentActionLabel: `${count} arbeidsstrømmer`,
          subagentRuntime: runtimes,
          planningTasks: prev.planningTasks.map((task, index) => index === prev.activeStepIndex
            ? {
              ...appendNarrativeUpdate(task, message),
              chips: [
                ...(task.chips ?? []),
                ...subtasks.slice(0, 4).map((subtask, subIndex) => ({
                  id: String(subtask.id ?? `${Date.now()}-${subIndex}`),
                  tool: String(subtask.role ?? 'agent'),
                  label: repairMojibake(asText(subtask.task || subtask.role || 'Subagent')),
                  timestamp: Date.now(),
                })),
              ],
            }
            : task),
        }));
        break;
      }

      case 'subagent_status': {
        const runtime: AgentSubagentRuntime = {
          id: repairMojibake(asText(data.id || uuidv4())),
          role: repairMojibake(asText(data.role || 'agent')),
          status: (data.status as AgentSubagentRuntime['status']) || 'running',
          task: repairMojibake(asText(data.task || 'Deloppgave')),
          summary: repairMojibake(asText(data.summary)),
          warning: repairMojibake(asText(data.warning)),
          duration_ms: typeof data.duration_ms === 'number' ? data.duration_ms : undefined,
        };
        const statusText = runtime.status === 'running'
          ? `${runtime.role.replace(/_/g, ' ')} jobber med ${runtime.task.toLowerCase()}.`
          : runtime.status === 'fallback'
            ? `${runtime.role.replace(/_/g, ' ')} bytter strategi: ${runtime.warning || runtime.summary || runtime.task}`
            : `${runtime.role.replace(/_/g, ' ')} er ferdig med ${runtime.task.toLowerCase()}.`;
        setState(prev => {
          const subagentRuntime = [
            ...prev.subagentRuntime.filter(item => item.id !== runtime.id),
            runtime,
          ].slice(-12);
          const planningTasks = prev.planningTasks.map((task, index) => index === prev.activeStepIndex
            ? {
              ...appendNarrativeUpdate(
                task,
                runtime.status === 'running'
                  ? statusText
                  : runtime.status === 'done'
                    ? (runtime.summary || `${runtime.role.replace(/_/g, ' ')} er ferdig med deloppgaven.`)
                    : task.prose || ''
              ),
            }
            : task);
          updateAgentMessage(_convId, _agentMsgId, { agentSubagentRuntime: subagentRuntime });
          return {
            ...prev,
            currentActionLabel: runtime.status === 'running' ? runtime.role.replace(/_/g, ' ') : prev.currentActionLabel,
            planningTasks,
            subagentRuntime,
          };
        });
        if (runtime.status !== 'queued') {
          addSemanticEvent({
            kind: runtime.status === 'fallback' ? 'warning' : runtime.status === 'done' ? 'observation' : 'tool',
            title: runtime.role.replace(/_/g, ' '),
            text: runtime.summary || statusText,
            tool: runtime.role,
            status: runtime.status === 'fallback' ? 'error' : runtime.status === 'done' ? 'done' : 'running',
          }, _convId, _agentMsgId);
        }
        break;
      }

      case 'subagent_started': {
        const role = repairMojibake(asText(data.role || 'agent'));
        const task = repairMojibake(asText(data.task || 'Jobber med deloppgave'));
        const query = repairMojibake(asText(data.query));
        addLog({ type: 'tool_call', message: `${role}: ${task}`, tool: role });
        addSemanticEvent({
          kind: 'tool',
          title: role.replace(/_/g, ' '),
          text: query ? `${task}: ${query}` : task,
          tool: role,
          status: 'running',
        }, _convId, _agentMsgId);
        setState(prev => ({ ...prev, currentActionLabel: role.replace(/_/g, ' ') }));
        break;
      }

      case 'subagent_completed': {
        const role = repairMojibake(asText(data.role || 'agent'));
        const task = repairMojibake(asText(data.task || 'Deloppgave'));
        const success = Boolean(data.success);
        const durationMs = Number(data.duration_ms ?? 0);
        const summary = repairMojibake(asText(data.summary));
        const message = success
          ? `${task} er ferdig (${Math.max(1, Math.round(durationMs / 1000))}s).`
          : `${task} feilet, jeg bruker en annen vei videre.`;
        addLog({ type: success ? 'tool_result' : 'error', message: `${role}: ${message}`, tool: role, success });
        addSemanticEvent({
          kind: success ? 'observation' : 'warning',
          title: success ? 'Agent ferdig' : 'Agent bytter strategi',
          text: summary || message,
          tool: role,
          status: success ? 'done' : 'error',
        }, _convId, _agentMsgId);
        setState(prev => ({
          ...prev,
          planningTasks: prev.planningTasks.map((planningTask, index) => index === prev.activeStepIndex
            ? {
              ...appendNarrativeUpdate(planningTask, summary || message),
              chips: (planningTask.chips ?? []).map(chip =>
                chip.tool === role && chip.success === undefined ? { ...chip, success } : chip
              ),
            }
            : planningTask),
        }));
        break;
      }

      case 'semantic_progress': {
        const stepIdx = (data.step_index as number | undefined) ?? undefined;
        const kind = (data.kind as AgentSemanticEvent['kind'] | undefined) ?? 'observation';
        const status = (data.status as AgentSemanticEvent['status'] | undefined) ?? 'info';
        const text = repairMojibake(asText(data.text || data.message));
        const title = repairMojibake(asText(data.title));
        const tool = repairMojibake(asText(data.tool));
        const success = data.success as boolean | undefined;
        if (!text) break;

        addSemanticEvent({
          kind,
          title: title || undefined,
          text,
          tool: tool || undefined,
          status,
        }, _convId, _agentMsgId);

        setState(prev => {
          const index = typeof stepIdx === 'number' ? stepIdx : prev.activeStepIndex;
          const planningTasks = prev.planningTasks.map((task, taskIndex) => {
            if (taskIndex !== index) return task;
            return {
              ...appendNarrativeUpdate(task, text),
              status: status === 'error'
                ? 'active' as const
                : status === 'done' && success !== false
                  ? 'done' as const
                  : 'active' as const,
            };
          });
          return {
            ...prev,
            activeStepIndex: index,
            currentActionLabel: title || prev.currentActionLabel || 'Tenker',
            planningTasks,
          };
        });
        break;
      }

      case 'multi_agent_complete': {
        const subtasks = Number(data.subtasks ?? 0);
        const durationMs = Number(data.duration_ms ?? 0);
        const message = `Multi-agent grunnarbeid er ferdig: ${subtasks} deloppgaver på ${Math.max(1, Math.round(durationMs / 1000))}s.`;
        addLog({ type: 'log', message });
        addSemanticEvent({
          kind: 'done',
          title: 'Grunnarbeid klart',
          text: message,
          status: 'done',
        }, _convId, _agentMsgId);
        break;
      }

      case 'log':
        addLog({ type: data.level === 'thinking' ? 'thinking' : 'log', message: data.message as string });
        if (data.message) {
          const logMessage = repairMojibake(data.message as string);
          const isNoisy = /^(Kjører|✓|Feil:|web_search|browser|terminal|LLM-feil|Advarsel:)/i.test(logMessage);
          if (!isNoisy) {
            addSemanticEvent({
              kind: data.level === 'error' ? 'warning' : 'observation',
              text: logMessage,
              status: data.level === 'error' ? 'error' : 'info',
            }, _convId, _agentMsgId);
          }
        }
        break;

      case 'browser_start': {
        const task = repairMojibake(asText(data.task || data.message || 'Sine åpner nettleseren.'));
        const frame: AgentBrowserFrame = {
          id: uuidv4(),
          message: task,
          status: 'starting',
          timestamp: Date.now(),
        };
        setState(prev => ({
          ...prev,
          browserFrame: frame,
          browserEvents: [...prev.browserEvents, frame].slice(-30),
          browserSession: browserSessionFromFrame(prev.browserSession, frame, 'active'),
          currentActionLabel: 'Bruker nettleser',
        }));
        addSemanticEvent({
          kind: 'browser',
          title: 'Nettleser',
          text: task,
          tool: 'browser',
          status: 'running',
        }, _convId, _agentMsgId);
        break;
      }

      case 'browser_screenshot': {
        const actionText = repairMojibake(asText(data.message || data.action || data.title || data.url));
        const frame: AgentBrowserFrame = {
          id: uuidv4(),
          screenshot: asText(data.screenshot),
          url: repairMojibake(asText(data.url)),
          title: repairMojibake(asText(data.title)),
          message: actionText,
          step: typeof data.step === 'number' ? data.step : undefined,
          status: 'running',
          timestamp: Date.now(),
        };
        const now = Date.now();
        setState(prev => ({
          ...prev,
          browserFrame: { ...prev.browserFrame, ...frame },
          browserEvents: [...prev.browserEvents, frame].slice(-30),
          browserSession: browserSessionFromFrame(prev.browserSession, frame, prev.browserSession?.status === 'takeover_active' ? 'takeover_active' : 'active'),
          currentActionLabel: 'Bruker nettleser',
          planningTasks: actionText && now - (prev.browserFrame?.timestamp ?? 0) > 2500
            ? prev.planningTasks.map((task, index) => index === prev.activeStepIndex
              ? appendNarrativeUpdate(task, actionText)
              : task)
            : prev.planningTasks,
        }));
        break;
      }

      case 'browser_action': {
        const action = repairMojibake(asText(data.action || data.message || 'Leser siden.'));
        const frame: AgentBrowserFrame = {
          id: uuidv4(),
          action,
          url: repairMojibake(asText(data.url)),
          message: action,
          step: typeof data.step === 'number' ? data.step : undefined,
          status: 'running',
          timestamp: Date.now(),
        };
        setState(prev => ({
          ...prev,
          browserFrame: { ...prev.browserFrame, ...frame },
          browserEvents: [...prev.browserEvents, frame].slice(-30),
          browserSession: browserSessionFromFrame(prev.browserSession, frame, prev.browserSession?.status === 'takeover_active' ? 'takeover_active' : 'active'),
          currentActionLabel: 'Bruker nettleser',
          planningTasks: action
            ? prev.planningTasks.map((task, index) => index === prev.activeStepIndex
              ? appendNarrativeUpdate(task, action)
              : task)
            : prev.planningTasks,
        }));
        addSemanticEvent({
          kind: 'browser',
          title: 'Nettleser',
          text: action,
          tool: 'browser',
          status: 'running',
        }, _convId, _agentMsgId);
        break;
      }

      case 'browser_done': {
        const frame: AgentBrowserFrame = {
          id: uuidv4(),
          message: repairMojibake(asText(data.message || 'Nettlesersteget er ferdig.')),
          status: 'done',
          timestamp: Date.now(),
        };
        setState(prev => ({
          ...prev,
          browserFrame: prev.browserFrame ? { ...prev.browserFrame, ...frame } : frame,
          browserEvents: [...prev.browserEvents, frame].slice(-30),
          browserSession: browserSessionFromFrame(prev.browserSession, frame, 'done'),
          planningTasks: frame.message
            ? prev.planningTasks.map((task, index) => index === prev.activeStepIndex
              ? appendNarrativeUpdate(task, frame.message || '')
              : task)
            : prev.planningTasks,
        }));
        addSemanticEvent({
          kind: 'browser',
          title: 'Nettleser',
          text: frame.message || 'Nettlesersteget er ferdig.',
          tool: 'browser',
          status: 'done',
        }, _convId, _agentMsgId);
        break;
      }

      case 'browser_error':
      case 'browser_needs_takeover': {
        const message = repairMojibake(asText(data.message || data.error || 'Nettleseren trenger en annen strategi.'));
        const frame: AgentBrowserFrame = {
          id: uuidv4(),
          message,
          status: type === 'browser_needs_takeover' ? 'takeover' : 'error',
          timestamp: Date.now(),
        };
        setState(prev => ({
          ...prev,
          status: type === 'browser_needs_takeover' ? 'needs_takeover' : prev.status,
          pauseReason: type === 'browser_needs_takeover' ? asText(data.reason || 'takeover_required') : prev.pauseReason,
          browserFrame: prev.browserFrame ? { ...prev.browserFrame, ...frame } : frame,
          browserEvents: [...prev.browserEvents, frame].slice(-30),
          browserSession: browserSessionFromFrame(prev.browserSession, frame, type === 'browser_needs_takeover' ? 'needs_takeover' : 'error'),
        }));
        addSemanticEvent({
          kind: 'warning',
          title: type === 'browser_needs_takeover' ? 'Trenger hjelp i nettleser' : 'Nettleserfeil',
          text: message,
          tool: 'browser',
          status: type === 'browser_needs_takeover' ? 'running' : 'error',
        }, _convId, _agentMsgId);
        break;
      }

      case 'browser_takeover_active':
      case 'browser_takeover_ended': {
        const isActiveTakeover = type === 'browser_takeover_active';
        const message = isActiveTakeover
          ? 'Nettleseren venter på manuell overtakelse.'
          : 'Nettleseren er tilbake hos Sine.';
        const frame: AgentBrowserFrame = {
          id: uuidv4(),
          message,
          status: isActiveTakeover ? 'takeover' : 'running',
          timestamp: Date.now(),
        };
        setState(prev => ({
          ...prev,
          status: isActiveTakeover ? 'takeover_active' : 'running',
          pauseReason: isActiveTakeover ? 'takeover_active' : null,
          browserFrame: prev.browserFrame ? { ...prev.browserFrame, ...frame } : frame,
          browserEvents: [...prev.browserEvents, frame].slice(-30),
          browserSession: browserSessionFromFrame(prev.browserSession, frame, isActiveTakeover ? 'takeover_active' : 'active'),
          currentActionLabel: isActiveTakeover ? 'Takeover aktiv' : 'Fortsetter etter takeover',
        }));
        addSemanticEvent({
          kind: isActiveTakeover ? 'warning' : 'browser',
          title: 'Nettleser',
          text: message,
          tool: 'browser',
          status: 'running',
        }, _convId, _agentMsgId);
        break;
      }


      case 'tool_call': {
        const toolName = data.tool as string;
        const taskId = uuidv4();
        const backendLabel = data.label as string | undefined;
        const label = backendLabel || getTaskLabel(toolName, data.args as Record<string, unknown>);
        const stepIdx = (data.step_index as number | undefined) ?? 0;
        const newTask: AgentTask = { id: taskId, label, status: 'running', tool: toolName };
        const toolProse = semanticToolText(toolName, data.args as Record<string, unknown>, label);

        activeTasksRef.current.set(taskId, newTask);
        addLog({ type: 'tool_call', message: `Kjører ${toolName}`, tool: toolName, args: data.args as Record<string, unknown> });

        const actionLabelMap: Record<string, string> = {
          write_file: 'Skriver',
          read_file: 'Leser',
          list_files: 'Lister filer',
          terminal: 'Kjører terminal',
          web_search: 'Søker',
          git_clone: 'Kloner repo',
          browser: 'Bruker nettleser',
          gmail_read: 'Leser e-post',
          gmail_send: 'Sender e-post',
          github_list_repos: 'Leser GitHub',
          github_read_file: 'Leser GitHub-fil',
          github_push_file: 'Pusher til GitHub',
          github_create_repo: 'Oppretter repo',
          deploy_preview: 'Publiserer preview',
          mcp_list_tools: 'Laster MCP-tools',
          mcp_call_tool: 'Kjører MCP-tool',
          run_code: 'Kjører kode',
        };
        const actionLabel = actionLabelMap[toolName] ?? 'Implementerer';

        // #11/#12: Add action chip to the active planning step
        const chip: ActionChip = {
          id: taskId,
          tool: toolName,
          label,
          timestamp: Date.now(),
        };

        setState(prev => {
          const updatedTasks = [...prev.liveTasks, newTask];
          const isTerminalTool = toolName === 'terminal' || toolName === 'run_python';
          const terminalFrames = isTerminalTool
            ? [...prev.terminalFrames, {
              id: taskId,
              command: repairMojibake(getTerminalCommand(toolName, data.args as Record<string, unknown>)),
              status: 'running' as const,
              timestamp: Date.now(),
            }].slice(-20)
            : prev.terminalFrames;
          // Add chip to the step at stepIdx
          const planningTasks = prev.planningTasks.map((t, i) => {
            if (i === stepIdx) {
              return { ...appendNarrativeUpdate(t, toolProse), status: 'active' as const, chips: [...(t.chips ?? []), chip] };
            }
            if (t.status === 'active') {
              return { ...t, status: 'done' as const };
            }
            return t;
          });
          if (_convId && _agentMsgId) {
            updateAgentMessage(_convId, _agentMsgId, { agentTasks: updatedTasks });
          }
          return {
            ...prev,
            status: 'running',
            liveTasks: updatedTasks,
            planningTasks,
            activeStepIndex: stepIdx,
            currentActionLabel: actionLabel,
            terminalFrames,
          };
        });
        break;
      }
      case 'tool_result': {
        const toolName = data.tool as string;
        const success = data.success as boolean;
        const stepIdx = (data.step_index as number | undefined) ?? 0;
        const recoverable = (data.recoverable as boolean | undefined) ?? true;
        const userMessage = data.user_message as string | undefined;
        const nextAction = data.next_action as string | undefined;
        const resultText = semanticResultText(toolName, success, data);
        addLog({
          type: 'tool_result',
          message: success ? `âœ“ ${toolName}` : (userMessage || `âœ— ${toolName}: ${data.error}`),
          tool: toolName,
          success,
          recoverable,
          nextAction,
        });

        // #12: Mark chip as success/error on the planning step
        setState(prev => {
          const tasks = [...prev.liveTasks];
          const idx = tasks.findLastIndex(t => t.tool === toolName && t.status === 'running');
          if (idx >= 0) {
            tasks[idx] = { ...tasks[idx], status: success ? 'done' : 'error' };
          }
          const isTerminalTool = toolName === 'terminal' || toolName === 'run_python';
          const lastRunningTerminalIdx = prev.terminalFrames.findLastIndex(frame => frame.status === 'running');
          const terminalFrames = isTerminalTool && lastRunningTerminalIdx >= 0
            ? prev.terminalFrames.map((frame, index) => index === lastRunningTerminalIdx
              ? {
                ...frame,
                status: success ? 'done' as const : 'error' as const,
                output: repairMojibake(asText(data.output || data.stdout || data.result)),
                error: repairMojibake(asText(data.error || data.stderr)),
              }
              : frame)
            : prev.terminalFrames;
          // Update chip success state
          const planningTasks = prev.planningTasks.map((t, i) => {
            if (i === stepIdx) {
              return {
                ...appendNarrativeUpdate(t, resultText),
                status: success ? 'done' as const : 'active' as const,
                chips: (t.chips ?? []).map(c =>
                  c.tool === toolName && c.success === undefined
                    ? { ...c, success }
                    : c
                ),
              };
            }
            return t;
          });
          if (_convId && _agentMsgId) {
            updateAgentMessage(_convId, _agentMsgId, { agentTasks: tasks });
          }
          return { ...prev, liveTasks: tasks, planningTasks, terminalFrames };
        });
        break;
      }

      case 'file_change': {
        const filePath = data.path as string;
        const fileAction = data.action as 'created' | 'modified';
        const agentFile = buildAgentFileFromPayload(data, runIdRef.current, fileAction)
          ?? buildAgentFileFromPath(filePath, runIdRef.current, fileAction);
        const runtimeArtifact = agentFileToRuntimeArtifact(agentFile, runIdRef.current, fileAction);

        setState(prev => {
          const liveFiles = [...prev.liveFiles.filter(f => f.path !== filePath), agentFile];
          const runtimeArtifacts = upsertArtifact(prev.runtime.artifacts, runtimeArtifact);
          const liveTasks = prev.liveTasks.map(t =>
            t.tool === 'write_file' && t.status === 'running'
              ? { ...t, filePath, status: 'done' as const }
              : t
          );
          if (_convId && _agentMsgId) {
            updateAgentMessage(_convId, _agentMsgId, { agentFiles: liveFiles, agentTasks: liveTasks });
          }
          return {
            ...prev,
            files: [...prev.files.filter(f => f.path !== filePath), { path: filePath, action: fileAction }],
            liveFiles,
            liveTasks,
            runtime: {
              ...prev.runtime,
              runId: runIdRef.current ?? prev.runtime.runId,
              artifacts: runtimeArtifacts,
            },
          };
        });
        addLog({ type: 'file_change', message: `${fileAction === 'created' ? 'Opprettet' : 'Endret'}: ${filePath}` });
        addSemanticEvent({
          kind: 'file',
          title: fileAction === 'created' ? 'Fil opprettet' : 'Fil oppdatert',
          text: `${filePath} er ${fileAction === 'created' ? 'opprettet' : 'oppdatert'}.`,
          tool: 'write_file',
          status: 'done',
        }, _convId, _agentMsgId);
        break;
      }

      case 'artifact.created': {
        const fileAction = (data.action as 'created' | 'modified' | undefined) ?? 'created';
        const agentFile = buildAgentFileFromPayload(data, runIdRef.current, fileAction);
        if (!agentFile) break;
        const runtimeArtifact = agentFileToRuntimeArtifact(agentFile, runIdRef.current, fileAction);

        setState(prev => {
          const liveFiles = [...prev.liveFiles.filter(f => f.path !== agentFile.path), agentFile];
          const runtimeArtifacts = upsertArtifact(prev.runtime.artifacts, runtimeArtifact);
          if (_convId && _agentMsgId) {
            updateAgentMessage(_convId, _agentMsgId, { agentFiles: liveFiles });
          }
          return {
            ...prev,
            liveFiles,
            runtime: {
              ...prev.runtime,
              runId: runIdRef.current ?? prev.runtime.runId,
              artifacts: runtimeArtifacts,
            },
          };
        });
        break;
      }

      case 'approval_needed':
      case 'approval.requested': {
        const approvalPayload = {
          tool: data.tool as string,
          args: data.args as Record<string, unknown>,
          description: data.description as string,
          riskLevel: data.risk_level as string | undefined,
          reason: data.reason as string | undefined,
        };
        setState(prev => {
          const approvalKey = `${approvalPayload.tool}:${approvalPayload.reason ?? ''}:${approvalPayload.description}`;
          const approvals = prev.runtime.approvals.some(approval => (
            `${String(approval.tool ?? '')}:${String(approval.reason ?? '')}:${String(approval.description ?? '')}` === approvalKey
          ))
            ? prev.runtime.approvals
            : [...prev.runtime.approvals, data];

          return {
            ...prev,
            status: 'waiting_approval',
            pendingApproval: approvalPayload,
            runtime: {
              ...prev.runtime,
              status: 'waiting_approval',
              approvals,
            },
          };
        });
        if (type === 'approval_needed') {
          addSemanticEvent({
            kind: 'warning',
            title: 'Trenger godkjenning',
            text: data.description as string,
            tool: data.tool as string,
            status: 'running',
          }, _convId, _agentMsgId);
        }
        break;
      }

      case 'complete': {
        receivedCompleteRef.current = true;
        // Hent leveransefiler fra backend (med innhold og downloadUrl)
        const deliveryFiles = data.files as Array<{
          name: string; path: string; type: string; size: string; content?: string;
        }> | undefined;
        const suggestions = (data.suggestions as string[] | undefined)?.map(repairMojibake);
        const sourcePack = (data.source_pack as AgentSourceChip[] | undefined) ?? [];
        const responseMode = (data.response_mode as AgentIntentDecision['response_mode'] | undefined) ?? 'default';
        const runId = runIdRef.current;
        const completionMetrics = data.metrics as AgentState['metrics'] | undefined;
        const completionStatus = (data.status as AgentStatus | undefined) ?? 'completed';
        const isFailureStatus = completionStatus === 'failed' || completionStatus === 'fatal_failed';
        const structuredOutput = data.structured_output as AgentStructuredOutput | undefined;
        const memorySummary = data.memory_summary as AgentMemorySummary | undefined;
        const workflowSummary = data.workflow_summary as AgentWorkflowSummary | undefined;
        const completionMessage = repairMojibake(asText(data.message));
        const recurringProposal = (data.monitor_setup as AgentRecurringProposal | undefined) ?? deriveAgentRecurringProposal(
          taskRef.current,
          completionMessage,
          responseMode,
          sourcePack,
          structuredOutput,
        );

        const agentFiles: AgentFile[] = (deliveryFiles ?? []).map(f => ({
          name: f.name,
          path: f.path,
          type: f.type as AgentFile['type'],
          size: f.size,
          content: f.content,
          downloadUrl: runId ? `${API_BASE}/api/agent/${runId}/download/${f.path}` : undefined,
          runId: runId ?? undefined,
        }));
        const completionArtifacts = agentFiles.map(file => agentFileToRuntimeArtifact(file, runId, 'created'));

        setState(prev => ({
          ...prev,
          status: completionStatus,
          pendingApproval: null,
          liveTasks: prev.liveTasks.map(t => t.status === 'running' ? { ...t, status: isFailureStatus ? 'error' as const : 'done' as const } : t),
          liveFiles: agentFiles.length > 0 ? agentFiles : prev.liveFiles,
          suggestions: suggestions ?? [],
          sourcePack,
          responseMode,
          recurringProposal,
          intentDecision: prev.intentDecision,
          selectedCapabilities: prev.selectedCapabilities,
          planningTasks: prev.planningTasks.map(t => {
            if (!isFailureStatus) return { ...t, status: 'done' as const };
            return t.status === 'done' ? t : { ...t, status: 'error' as const };
          }),
          currentActionLabel: completionStatus === 'completed' ? 'Ferdig' : isFailureStatus ? 'Stoppet' : 'Fullført med advarsler',
          metrics: completionMetrics ?? prev.metrics,
          runtime: {
            ...prev.runtime,
            runId: runId ?? prev.runtime.runId,
            status: completionStatus === 'completed'
              ? 'completed'
              : isFailureStatus
                ? 'failed'
                : prev.runtime.status,
            artifacts: completionArtifacts.reduce(
              (artifacts, artifact) => upsertArtifact(artifacts, artifact),
              prev.runtime.artifacts,
            ),
          },
        }));
        addLog({
          type: isFailureStatus ? 'error' : 'log',
          message: isFailureStatus ? 'Kjøringen stoppet før leveranse.' : 'Oppgave fullført',
        });
        addSemanticEvent({
          kind: completionStatus === 'completed' ? 'done' : 'warning',
          title: isFailureStatus
            ? 'Kjøringen stoppet'
            : completionStatus === 'completed'
              ? 'Leveranse klar'
              : 'Leveranse klar med forbehold',
          text: isFailureStatus
            ? (completionMessage || 'Sine stoppet før oppgaven var ferdig. Vi kan fortsette fra samme punkt.')
            : agentFiles.length > 0
              ? `Jeg har laget ${agentFiles.length} fil${agentFiles.length === 1 ? '' : 'er'} og oppsummerer funnene.`
              : 'Jeg oppsummerer funnene og hva som gjenstår.',
          status: isFailureStatus ? 'error' : completionStatus === 'completed' ? 'done' : 'info',
        }, _convId, _agentMsgId);

        // #1: Auto-save completed run to Supabase history (non-blocking)
        if (runId) {
          getSupabase().auth.getSession().then(({ data: { session } }) => {
            if (session?.access_token) {
              fetch(`${API_BASE}/api/agent/history/save?run_id=${runId}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${session.access_token}` },
              }).catch(() => { /* silent fail â€” history is non-critical */ });
            }
          });
        }

        // Oppdater agent-meldingen med filer og forslag
        if (_convId && _agentMsgId) {
          // Vi bruker updateMessage via AppContext for Ã¥ lagre filer og forslag
          // Dette gjÃ¸res via en custom event siden updateMessage kun stÃ¸tter content
          const event = new CustomEvent('agent-complete', {
            detail: {
              convId: _convId,
              msgId: _agentMsgId,
              files: agentFiles,
              suggestions: suggestions ?? [],
              sourcePack,
              intentDecision: intentDecisionRef.current,
              selectedCapabilities: selectedCapabilitiesRef.current,
              structuredOutput,
              memorySummary,
              workflowSummary,
              responseMode,
              recurringProposal,
              semanticEvents: semanticEventsRef.current,
              message: completionMessage,
              status: completionStatus,
            }
          });
          window.dispatchEvent(event);
        }
        break;
      }

      case 'error':
        receivedCompleteRef.current = true;
        finalizeAgentMessage(
          _convId,
          _agentMsgId,
          repairMojibake(data.message as string),
          ((data.fatal as boolean | undefined) ? 'fatal_failed' : 'failed'),
        );
        break;
    }
  }, [addLog, addSemanticEvent, finalizeAgentMessage, updateAgentMessage]);

  const hydrateAgentRunSnapshot = useCallback(async (
    runId: string,
    conversationId: string,
    agentMessageId: string,
    task = '',
  ) => {
    const snapshot = await fetchAgentRunState(runId);
    if (!snapshot) return null;

    const files = normalizeRunStateFiles(snapshot);
    const events = normalizeRunStateEvents(snapshot);
    const planningTasks = normalizeRunStatePlanningTasks(snapshot);
    const computer = normalizeRunStateComputerFrames(snapshot);
    const snapshotStatus = normalizeAgentStatus(snapshot.status, 'running');
    const pauseReason = snapshot.pause_reason ?? null;
    const semanticSnapshotStatus = normalizeAgentStatus(
      pauseReason === 'takeover_active' ? 'takeover_active' : pauseReason === 'takeover_required' ? 'needs_takeover' : snapshotStatus,
      snapshotStatus,
    );
    const visibleStatus = TERMINAL_AGENT_STATUSES.has(snapshotStatus)
      || snapshotStatus === 'interrupted'
      || snapshotStatus === 'resume_available'
      || snapshotStatus === 'stale'
      ? snapshotStatus
      : semanticSnapshotStatus === 'needs_takeover' || semanticSnapshotStatus === 'takeover_active'
        ? semanticSnapshotStatus
        : 'recovering';

    const hydratedContent = selectHydratedAgentContent(undefined, snapshot);
    updateAgentMessage(conversationId, agentMessageId, {
      agentRunId: runId,
      agentFiles: files,
      agentEvents: events,
      agentPlanningTasks: planningTasks,
      agentStatus: visibleStatus,
      agentSuggestions: snapshot.suggestions ?? [],
      ...(hydratedContent ? { content: hydratedContent } : {}),
    });

    const knownSeqBeforeSnapshot = lastSequenceByRunRef.current[runId] ?? 0;
    const lastSeq = typeof snapshot.last_sequence_number === 'number'
      ? snapshot.last_sequence_number
      : knownSeqBeforeSnapshot;
    lastSequenceByRunRef.current[runId] = Math.max(knownSeqBeforeSnapshot, lastSeq);

    setState(prev => ({
      ...prev,
      runId,
      agentMessageId,
      currentTask: task || prev.currentTask || 'Gjenoppretter agent-kjøring',
      status: visibleStatus,
      pauseReason,
      liveFiles: files.length > 0 ? files : prev.liveFiles,
      planningTasks: planningTasks.length > 0 ? planningTasks : prev.planningTasks,
      logs: events.length > 0
        ? events.map(event => ({
            id: event.id,
            type: event.type === 'tool_result' ? 'tool_result' : event.type === 'tool_call' ? 'tool_call' : 'log',
            message: event.label,
            tool: event.tool,
            success: event.success,
            timestamp: event.timestamp,
          }))
        : prev.logs,
      browserFrame: computer.browserFrame ?? prev.browserFrame,
      browserEvents: computer.browserEvents.length > 0 ? computer.browserEvents : prev.browserEvents,
      browserSession: browserSessionFromFrame(
        prev.browserSession,
        computer.browserFrame ?? prev.browserFrame,
        visibleStatus === 'takeover_active'
          ? 'takeover_active'
          : visibleStatus === 'needs_takeover'
            ? 'needs_takeover'
            : undefined,
      ),
      terminalFrames: computer.terminalFrames.length > 0 ? computer.terminalFrames : prev.terminalFrames,
      currentActionLabel: visibleStatus === 'recovering' ? 'Kobler til kjøringen igjen' : prev.currentActionLabel,
      runtime: {
        ...prev.runtime,
        runId,
        conversationId,
        status: runtimeStatusFromAgentStatus(visibleStatus),
        lastSeqByRun: {
          ...prev.runtime.lastSeqByRun,
          [runId]: lastSeq,
        },
      },
    }));

    const knownSeqBeforeReplay = knownSeqBeforeSnapshot;
    const replay = await fetchAgentRunEvents(runId, knownSeqBeforeReplay);
    for (const event of replay.events) {
      handleAgentEvent(event, conversationId, agentMessageId);
    }
    if (replay.last_sequence_number > knownSeqBeforeReplay) {
      lastSequenceByRunRef.current[runId] = replay.last_sequence_number;
    }

    return snapshot;
  }, [handleAgentEvent, updateAgentMessage]);

  const resumeAgentRun = useCallback(async (
    runId: string,
    conversationId: string,
    agentMessageId: string,
    task = '',
  ) => {
    if (!runId || runIdRef.current === runId || isLiveAgentStatus(state.status)) return;
    const { data: { session } } = await getSupabase().auth.getSession();
    const accessToken = session?.access_token || '';
    accessTokenRef.current = accessToken;
    runIdRef.current = runId;
    convIdRef.current = conversationId;
    agentMsgIdRef.current = agentMessageId;
    taskRef.current = task || taskRef.current || state.currentTask || 'Gjenoppretter agent-kjøring';
    receivedCompleteRef.current = false;

    setState(prev => ({
      ...prev,
      runId,
      agentMessageId,
      currentTask: task || prev.currentTask || 'Gjenoppretter agent-kjøring',
      status: 'recovering',
      currentActionLabel: 'Kobler til kjøringen igjen',
      runtime: {
        ...prev.runtime,
        runId,
        status: 'recovering',
      },
    }));

    const snapshot = await hydrateAgentRunSnapshot(runId, conversationId, agentMessageId, task);
    const resumeDecision = await requestAgentRunResume(runId).catch(() => null);
    if (resumeDecision && !resumeDecision.can_reconnect) {
      const recoveryStatus = normalizeAgentStatus(resumeDecision.status, snapshot?.status ? normalizeAgentStatus(snapshot.status, 'resume_available') : 'resume_available');
      updateAgentMessage(conversationId, agentMessageId, {
        agentRunId: runId,
        agentStatus: recoveryStatus,
      });
      setState(prev => ({
        ...prev,
        status: recoveryStatus,
        currentActionLabel: resumeDecision.can_retry ? 'Kjøringen kan startes på nytt' : undefined,
        runtime: {
          ...prev.runtime,
          runId,
          conversationId,
          status: runtimeStatusFromAgentStatus(recoveryStatus),
          lastSeqByRun: {
            ...prev.runtime.lastSeqByRun,
            [runId]: Math.max(prev.runtime.lastSeqByRun[runId] ?? 0, resumeDecision.last_sequence_number ?? 0),
          },
        },
      }));
      runIdRef.current = null;
      return;
    }

    const wsUrl = accessToken
      ? `${WS_BASE}/api/agent/ws/${runId}?token=${encodeURIComponent(accessToken)}`
      : `${WS_BASE}/api/agent/ws/${runId}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setState(prev => ({
        ...prev,
        status: 'running',
        currentActionLabel: prev.currentActionLabel === 'Kobler til kjøringen igjen' ? 'Tenker' : prev.currentActionLabel,
        runtime: { ...prev.runtime, status: 'running' },
      }));
      ws.send(JSON.stringify({
        action: 'reconnect',
        resume: true,
        task,
        conversation_id: conversationId,
        message_id: agentMessageId,
        user_memory: [],
        conversation_history: [],
        last_sequence_number: lastSequenceByRunRef.current[runId] ?? 0,
      }));
    };
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        handleAgentEvent(msg, conversationId, agentMessageId);
      } catch {
        // Reconnect replay should never break the visible chat.
      }
    };
    ws.onerror = () => {
      addLog({ type: 'log', message: 'Kunne ikke gjenopprette live-tilkobling. Viser lagret replay i stedet.' });
    };
    ws.onclose = () => {
      setState(prev => {
        if (!isLiveAgentStatus(prev.status) || receivedCompleteRef.current) {
          return prev;
        }
        return {
          ...prev,
          currentActionLabel: 'Kobler til igjen',
          status: 'recovering',
          runtime: { ...prev.runtime, status: 'recovering' },
        };
      });
    };
  }, [addLog, handleAgentEvent, hydrateAgentRunSnapshot, state.currentTask, state.status, updateAgentMessage]);

  const approveAction = useCallback(async (approved: boolean) => {
    if (!runIdRef.current) return;
    const headers = await getAuthHeaders();
    await fetch(`${API_BASE}/api/agent/${runIdRef.current}/approve`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ approved, run_id: runIdRef.current }),
    });
    setState(prev => ({ ...prev, pendingApproval: null, status: 'running' }));
  }, []);

  const stopAgent = useCallback(async () => {
    if (wsRef.current) wsRef.current.send(JSON.stringify({ action: 'stop' }));
    if (runIdRef.current) await fetch(`${API_BASE}/api/agent/${runIdRef.current}/stop`, { method: 'POST' });
    setState(prev => ({ ...prev, status: 'stopped' }));
  }, []);

  const requestBrowserTakeover = useCallback(async (runId?: string): Promise<void> => {
    const rid = runId ?? runIdRef.current;
    if (!rid) return;
    const headers = await getAuthHeaders();
    await fetch(`${API_BASE}/api/agent/${rid}/browser/takeover`, {
      method: 'POST',
      headers,
    });
    setState(prev => {
      const frame: AgentBrowserFrame = prev.browserFrame
        ? { ...prev.browserFrame, status: 'takeover', message: 'Brukeren tar kontroll i nettleseren.', timestamp: Date.now() }
        : { id: uuidv4(), status: 'takeover', message: 'Brukeren tar kontroll i nettleseren.', timestamp: Date.now() };
      return {
        ...prev,
        status: 'takeover_active',
        browserFrame: frame,
        browserSession: browserSessionFromFrame(prev.browserSession, frame, 'takeover_active'),
        currentActionLabel: 'Takeover aktiv',
        pauseReason: 'takeover_active',
        runtime: {
          ...prev.runtime,
          status: 'takeover_active',
        },
      };
    });
  }, []);

  const resumeBrowserFromTakeover = useCallback(async (runId?: string): Promise<void> => {
    const rid = runId ?? runIdRef.current;
    if (!rid) return;
    const headers = await getAuthHeaders();
    await fetch(`${API_BASE}/api/agent/${rid}/browser/resume`, {
      method: 'POST',
      headers,
    });
    setState(prev => {
      const frame: AgentBrowserFrame | null = prev.browserFrame
        ? { ...prev.browserFrame, status: 'running', message: 'Nettleseren er tilbake hos Sine.', timestamp: Date.now() }
        : null;
      return {
        ...prev,
        status: prev.status === 'takeover_active' || prev.status === 'needs_takeover' || prev.status === 'waiting_approval' ? 'running' : prev.status,
        browserFrame: frame ?? prev.browserFrame,
        browserSession: browserSessionFromFrame(prev.browserSession, frame, 'active'),
        currentActionLabel: 'Fortsetter etter takeover',
        pauseReason: null,
        runtime: {
          ...prev.runtime,
          status: 'running',
        },
      };
    });
  }, []);

  const fetchFileContent = useCallback(async (filePath: string, runId?: string): Promise<string> => {
    const rid = runId ?? runIdRef.current;
    if (!rid) return '';
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/api/agent/${rid}/files/${encodeURIComponent(filePath)}`, { headers });
      if (!res.ok) return '';
      const data = await res.json();
      return data.content ?? '';
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    return () => { wsRef.current?.close(); };
  }, []);

  return { state, startAgent, resumeAgentRun, stopAgent, approveAction, fetchFileContent, requestBrowserTakeover, resumeBrowserFromTakeover };
}
